// Weather and events system configuration

export type WeatherType = 'sunny' | 'cloudy' | 'rainy' | 'stormy'

export interface WeatherDefinition {
  type: WeatherType
  name: string
  emoji: string
  outdoorModifier: number // Multiplier for outdoor court bookings
  description: string
}

export const WEATHER_DEFINITIONS: Record<WeatherType, WeatherDefinition> = {
  sunny: {
    type: 'sunny',
    name: 'Soleado',
    emoji: '☀️',
    outdoorModifier: 1.2,
    description: 'Dia perfecto para jugar al aire libre',
  },
  cloudy: {
    type: 'cloudy',
    name: 'Nublado',
    emoji: '☁️',
    outdoorModifier: 1.0,
    description: 'Tiempo agradable, sin sol directo',
  },
  rainy: {
    type: 'rainy',
    name: 'Lluvia',
    emoji: '🌧️',
    outdoorModifier: 0.3,
    description: 'Las pistas outdoor estan vacias',
  },
  stormy: {
    type: 'stormy',
    name: 'Tormenta',
    emoji: '⛈️',
    outdoorModifier: 0.0,
    description: 'Peligro! No se puede jugar fuera',
  },
}

// Event types
export type EventType =
  | 'tournament_offer'
  | 'celebrity_visit'
  | 'equipment_sale'
  | 'sponsorship'
  | 'court_damage'
  | 'staff_bonus'
  | 'health_inspection'
  | 'media_coverage'
  // New types
  | 'competitor_opens'
  | 'terrain_deal'
  | 'staff_conflict'
  | 'municipal_regulation'
  | 'pipe_burst'
  | 'noise_complaint'
  | 'electrical_failure'
  | 'investor_offer'
  | 'school_proposal'
  | 'customer_surge'
  | 'social_media_viral'
  | 'vandalism'
  | 'bank_ultimatum'
  | 'el_clasico'

// Persistent game effects
export interface GameEffect {
  id: string
  source: string
  type: 'income_modifier' | 'expense_modifier' | 'reputation_modifier' | 'demand_modifier'
  value: number
  expiresOnDay: number // 0 = permanent
  description: string
}

// Event chain definition
export interface PendingChain {
  eventId: string
  triggerDay: number
}

export interface GameEvent {
  id: string
  type: EventType
  title: string
  description: string
  options: EventOption[]
  day: number
}

export interface EventOption {
  text: string
  effect: () => void
  cost?: number
  reward?: number
}

export interface EventOptionDef {
  text: string
  effectDescription: string
  costMultiplier?: number
  rewardMultiplier?: number
  // Effects to apply when chosen
  effects?: Omit<GameEffect, 'id'>[]
  // Follow-up event chain
  followUpEventId?: string
  followUpDelay?: number
}

export interface EventDefinition {
  type: EventType
  title: string
  emoji: string
  description: string
  minDay: number
  probability: number
  minCourts?: number
  minReputation?: number
  minBuildings?: number
  minStaff?: number
  options: EventOptionDef[]
}

export const EVENT_DEFINITIONS: EventDefinition[] = [
  {
    // Triggered directly by GameStateSystem after 7 days in debt;
    // probability 0 keeps it out of the random pool
    type: 'bank_ultimatum',
    title: 'Ultimatum del banco',
    emoji: '🏦',
    description: 'Tu cuenta lleva una semana en numeros rojos. El banco te ofrece un rescate de emergencia a un interes abusivo. Si sigues en negativo una semana mas, el club entrara en bancarrota.',
    minDay: 1,
    probability: 0,
    options: [
      {
        text: 'Aceptar rescate (+8.000€)',
        effectDescription: 'Ingresos -15% durante 14 dias',
        rewardMultiplier: 8000,
        effects: [
          { source: 'Rescate bancario', type: 'income_modifier', value: 0.85, expiresOnDay: 0, description: '-15% ingresos por intereses del rescate' },
        ],
      },
      { text: 'Resistir sin ayuda', effectDescription: 'Quedan 7 dias para salir del rojo' },
    ],
  },
  {
    // The recurring rivalry set-piece. Triggered by the StorySystem mid-season
    // (probability 0 keeps it out of the random pool). A choice with real, but
    // test-safe, consequences (only applied when the player picks an option).
    type: 'el_clasico',
    title: 'El Clásico',
    emoji: '🎾',
    description: 'Se acerca el derbi contra el club de DT Rival. La grada arde y las redes echan humo. ¿Cómo afrontas la semana del clásico?',
    minDay: 1,
    probability: 0,
    options: [
      {
        text: 'Guerra de declaraciones en redes (€800)',
        effectDescription: 'Llenas las pistas una semana (+25% demanda), pero la campaña cuesta',
        costMultiplier: 800,
        effects: [
          { source: 'El Clásico', type: 'demand_modifier', value: 1.25, expiresOnDay: 0, description: 'Fiebre del derbi (+25% demanda)' },
        ],
      },
      {
        text: 'Precios de derbi el día del partido',
        effectDescription: '+€1.500 al instante, pero la afición se resiente (-10% demanda)',
        rewardMultiplier: 1500,
        effects: [
          { source: 'El Clásico', type: 'demand_modifier', value: 0.9, expiresOnDay: 0, description: 'Resaca del derbi (-10% demanda)' },
        ],
      },
      {
        text: 'Cabeza fría: foco en el juego',
        effectDescription: 'Buen ambiente de club (+10% demanda), sin coste',
        effects: [
          { source: 'El Clásico', type: 'demand_modifier', value: 1.1, expiresOnDay: 0, description: 'Unidad de vestuario (+10% demanda)' },
        ],
      },
    ],
  },
  // === ORIGINAL EVENTS (updated with effects) ===
  {
    type: 'tournament_offer',
    title: 'Oferta de Torneo',
    emoji: '🏆',
    description: 'Una federacion local quiere organizar un torneo en tu club.',
    minDay: 7,
    probability: 0.08,
    minCourts: 2,
    options: [
      {
        text: 'Aceptar (€500)',
        effectDescription: '+€2.000 ingresos, +0.5 reputacion',
        costMultiplier: 500,
        effects: [
          { source: 'Torneo local', type: 'demand_modifier', value: 1.15, expiresOnDay: 0, description: '+15% demanda durante torneo' }
        ],
      },
      { text: 'Rechazar', effectDescription: 'Sin cambios' },
    ],
  },
  {
    type: 'celebrity_visit',
    title: 'Visita VIP',
    emoji: '⭐',
    description: 'Un jugador profesional quiere entrenar en tu club.',
    minDay: 14,
    probability: 0.04,
    minReputation: 3,
    options: [
      {
        text: 'Dar pista gratis',
        effectDescription: '+1 reputacion, publicidad',
        effects: [
          { source: 'Visita VIP', type: 'demand_modifier', value: 1.2, expiresOnDay: 0, description: '+20% demanda por publicidad' }
        ],
        followUpEventId: 'media_coverage',
        followUpDelay: 5,
      },
      { text: 'Cobrar precio normal', effectDescription: '+€100' },
    ],
  },
  {
    type: 'equipment_sale',
    title: 'Oferta de Equipamiento',
    emoji: '🎾',
    description: 'Un distribuidor ofrece raquetas con descuento.',
    minDay: 3,
    probability: 0.07,
    options: [
      {
        text: 'Comprar lote (€300)',
        effectDescription: 'Pro Shop +50% ingresos 7 dias',
        costMultiplier: 300,
        effects: [
          { source: 'Lote equipamiento', type: 'income_modifier', value: 1.1, expiresOnDay: 0, description: '+10% ingresos temporalmente' }
        ],
      },
      { text: 'No interesa', effectDescription: 'Sin cambios' },
    ],
  },
  {
    type: 'sponsorship',
    title: 'Propuesta de Patrocinio',
    emoji: '💼',
    description: 'Una marca deportiva quiere poner su logo en tu club.',
    minDay: 21,
    probability: 0.03,
    minReputation: 3.5,
    options: [
      {
        text: 'Aceptar',
        effectDescription: '+€500/semana durante 4 semanas',
        rewardMultiplier: 500,
        effects: [
          { source: 'Patrocinio temporal', type: 'income_modifier', value: 1.05, expiresOnDay: 0, description: '+5% ingresos por patrocinio' }
        ],
      },
      { text: 'Negociar mas', effectDescription: '50% chance mejores condiciones' },
    ],
  },
  {
    type: 'court_damage',
    title: 'Dano en Pista',
    emoji: '⚠️',
    description: 'Una de tus pistas ha sufrido danos inesperados.',
    minDay: 5,
    probability: 0.05,
    minCourts: 1,
    options: [
      { text: 'Reparar ya (€200)', effectDescription: 'Pista operativa manana', costMultiplier: 200 },
      {
        text: 'Ignorar',
        effectDescription: '-10% condicion, menos ingresos 3 dias',
        effects: [
          { source: 'Pista danada', type: 'income_modifier', value: 0.85, expiresOnDay: 0, description: '-15% ingresos por pista danada' }
        ],
      },
    ],
  },
  {
    type: 'staff_bonus',
    title: 'Moral del Equipo',
    emoji: '😊',
    description: 'Tu equipo esta trabajando muy bien ultimamente.',
    minDay: 14,
    probability: 0.06,
    minStaff: 1,
    options: [
      {
        text: 'Dar bonus (€100)',
        effectDescription: '+20% productividad 7 dias',
        costMultiplier: 100,
        effects: [
          { source: 'Bonus equipo', type: 'income_modifier', value: 1.1, expiresOnDay: 0, description: '+10% ingresos por productividad' }
        ],
      },
      { text: 'Solo felicitar', effectDescription: 'Equipo contento, sin coste' },
    ],
  },
  {
    type: 'health_inspection',
    title: 'Inspeccion Sanitaria',
    emoji: '📋',
    description: 'El inspector sanitario viene a revisar las instalaciones.',
    minDay: 10,
    probability: 0.04,
    options: [
      {
        text: 'Prepararse (€150)',
        effectDescription: 'Inspeccion aprobada, +0.2 rep',
        costMultiplier: 150,
        effects: [
          { source: 'Inspeccion OK', type: 'reputation_modifier', value: 1.05, expiresOnDay: 0, description: 'Buena imagen publica' }
        ],
      },
      {
        text: 'Confiar en el estado actual',
        effectDescription: 'Riesgo de multa si no hay vestuarios',
      },
    ],
  },
  {
    type: 'media_coverage',
    title: 'Cobertura Mediatica',
    emoji: '📺',
    description: 'Un periodista local quiere hacer un reportaje sobre tu club.',
    minDay: 7,
    probability: 0.04,
    options: [
      {
        text: 'Aceptar entrevista',
        effectDescription: '+0.3 reputacion, mas clientes',
        effects: [
          { source: 'Cobertura mediatica', type: 'demand_modifier', value: 1.25, expiresOnDay: 0, description: '+25% demanda por publicidad' }
        ],
        followUpEventId: 'customer_surge',
        followUpDelay: 3,
      },
      { text: 'Declinar', effectDescription: 'Sin cambios' },
    ],
  },

  // === NEW DECISION EVENTS (tradeoffs) ===
  {
    type: 'competitor_opens',
    title: 'Competidor Cercano',
    emoji: '🏢',
    description: 'Un nuevo club de padel abre a 2km. Tus clientes podrian irse.',
    minDay: 30,
    probability: 0.03,
    minCourts: 2,
    options: [
      {
        text: 'Bajar precios 15% (7 dias)',
        effectDescription: 'Mantener clientes, menos margen',
        effects: [
          { source: 'Precios rebajados', type: 'income_modifier', value: 0.85, expiresOnDay: 0, description: '-15% ingresos por precios bajos' }
        ],
      },
      {
        text: 'Invertir en marketing (€500)',
        effectDescription: '+20% demanda 14 dias',
        costMultiplier: 500,
        effects: [
          { source: 'Campana marketing', type: 'demand_modifier', value: 1.2, expiresOnDay: 0, description: '+20% demanda por marketing' }
        ],
      },
      { text: 'No hacer nada', effectDescription: '-10% demanda temporal',
        effects: [
          { source: 'Competencia', type: 'demand_modifier', value: 0.9, expiresOnDay: 0, description: '-10% demanda por competencia' }
        ],
      },
    ],
  },
  {
    type: 'terrain_deal',
    title: 'Oferta de Terreno',
    emoji: '🏗️',
    description: 'Un propietario vecino ofrece su parcela al 50% de precio. Oferta limitada.',
    minDay: 20,
    probability: 0.02,
    options: [
      {
        text: 'Comprar parcela (€2.500)',
        effectDescription: 'Parcela al 50%! Expansion inmediata',
        costMultiplier: 2500,
      },
      { text: 'Dejarlo pasar', effectDescription: 'La oferta no volvera' },
    ],
  },
  {
    type: 'staff_conflict',
    title: 'Conflicto Laboral',
    emoji: '😤',
    description: 'Dos empleados tienen un desacuerdo grave. La tension afecta al ambiente.',
    minDay: 21,
    probability: 0.04,
    minStaff: 2,
    options: [
      {
        text: 'Mediar y team building (€200)',
        effectDescription: 'Equipo unido, +5% productividad',
        costMultiplier: 200,
        effects: [
          { source: 'Team building', type: 'income_modifier', value: 1.05, expiresOnDay: 0, description: '+5% ingresos por buen ambiente' }
        ],
      },
      {
        text: 'Ignorar',
        effectDescription: '-10% productividad 7 dias',
        effects: [
          { source: 'Conflicto laboral', type: 'income_modifier', value: 0.9, expiresOnDay: 0, description: '-10% ingresos por mal ambiente' }
        ],
      },
    ],
  },
  {
    type: 'municipal_regulation',
    title: 'Normativa Municipal',
    emoji: '📜',
    description: 'El ayuntamiento exige nuevas medidas de seguridad en instalaciones deportivas.',
    minDay: 28,
    probability: 0.03,
    minCourts: 2,
    options: [
      {
        text: 'Cumplir (€400)',
        effectDescription: 'Todo en regla, +0.2 reputacion',
        costMultiplier: 400,
        effects: [
          { source: 'Normativa cumplida', type: 'reputation_modifier', value: 1.03, expiresOnDay: 0, description: 'Buena reputacion institucional' }
        ],
      },
      {
        text: 'Arriesgar multa',
        effectDescription: '30% chance multa de €800',
      },
    ],
  },

  // === CRISIS EVENTS ===
  {
    type: 'pipe_burst',
    title: 'Rotura de Tuberias',
    emoji: '💧',
    description: 'Las tuberias de los vestuarios han reventado. Agua por todas partes.',
    minDay: 14,
    probability: 0.03,
    minBuildings: 1,
    options: [
      {
        text: 'Reparacion urgente (€500)',
        effectDescription: 'Arreglado en 1 dia',
        costMultiplier: 500,
      },
      {
        text: 'Reparacion economica (€200)',
        effectDescription: 'Vestuarios cerrados 3 dias',
        costMultiplier: 200,
        effects: [
          { source: 'Vestuarios cerrados', type: 'demand_modifier', value: 0.8, expiresOnDay: 0, description: '-20% demanda sin vestuarios' }
        ],
      },
    ],
  },
  {
    type: 'noise_complaint',
    title: 'Queja Vecinal',
    emoji: '🔊',
    description: 'Los vecinos se quejan del ruido en horario nocturno.',
    minDay: 14,
    probability: 0.04,
    minCourts: 3,
    options: [
      {
        text: 'Insonorizar (€800)',
        effectDescription: 'Problema resuelto permanentemente',
        costMultiplier: 800,
        effects: [
          { source: 'Insonorizacion', type: 'reputation_modifier', value: 1.05, expiresOnDay: 0, description: 'Buenas relaciones vecinales' }
        ],
      },
      {
        text: 'Limitar horario nocturno',
        effectDescription: '-20% ingresos nocturnos permanente',
        effects: [
          { source: 'Horario limitado', type: 'income_modifier', value: 0.9, expiresOnDay: 0, description: '-10% ingresos por horario limitado' }
        ],
      },
    ],
  },
  {
    type: 'electrical_failure',
    title: 'Fallo Electrico',
    emoji: '⚡',
    description: 'El sistema electrico ha fallado. Las pistas con LED no pueden funcionar de noche.',
    minDay: 10,
    probability: 0.03,
    minCourts: 1,
    options: [
      {
        text: 'Electricista urgente (€300)',
        effectDescription: 'Reparado en 1 dia',
        costMultiplier: 300,
      },
      {
        text: 'Esperar al lunes (gratis)',
        effectDescription: 'Sin iluminacion 3 dias',
        effects: [
          { source: 'Sin electricidad', type: 'income_modifier', value: 0.75, expiresOnDay: 0, description: '-25% ingresos sin iluminacion' }
        ],
      },
    ],
  },

  // === OPPORTUNITY EVENTS ===
  {
    type: 'investor_offer',
    title: 'Inversor Interesado',
    emoji: '💰',
    description: 'Un empresario quiere invertir €50.000 en tu club a cambio del 15% de ingresos futuros.',
    minDay: 42,
    probability: 0.02,
    minCourts: 3,
    minReputation: 3.5,
    options: [
      {
        text: 'Aceptar inversion',
        effectDescription: '+€50.000, pero -15% ingresos permanente',
        rewardMultiplier: 50000,
        effects: [
          { source: 'Inversor socio', type: 'income_modifier', value: 0.85, expiresOnDay: 0, description: '-15% ingresos (participacion inversor)' }
        ],
      },
      { text: 'Mantener independencia', effectDescription: 'Sin cambios' },
    ],
  },
  {
    type: 'school_proposal',
    title: 'Convenio Escolar',
    emoji: '🏫',
    description: 'Un colegio quiere un convenio para clases de padel matutinas entre semana.',
    minDay: 28,
    probability: 0.03,
    minCourts: 2,
    minStaff: 1,
    options: [
      {
        text: 'Aceptar convenio',
        effectDescription: '+€500/semana fijo, ocupa 1 pista mananas',
        effects: [
          { source: 'Convenio escolar', type: 'income_modifier', value: 1.08, expiresOnDay: 0, description: '+8% ingresos por convenio escolar' }
        ],
      },
      { text: 'Rechazar', effectDescription: 'Sin cambios' },
    ],
  },
  {
    type: 'customer_surge',
    title: 'Oleada de Clientes',
    emoji: '👥',
    description: 'Tu club se ha vuelto popular! Hay lista de espera para reservar.',
    minDay: 14,
    probability: 0.04,
    minCourts: 2,
    minReputation: 3,
    options: [
      {
        text: 'Subir precios 10%',
        effectDescription: 'Mas ingresos, algunos clientes se van',
        effects: [
          { source: 'Precios altos', type: 'income_modifier', value: 1.1, expiresOnDay: 0, description: '+10% ingresos por precios altos' },
          { source: 'Precios altos', type: 'demand_modifier', value: 0.95, expiresOnDay: 0, description: '-5% demanda por precios altos' },
        ],
      },
      {
        text: 'Mantener precios',
        effectDescription: '+0.3 reputacion por buen servicio',
        effects: [
          { source: 'Buen servicio', type: 'reputation_modifier', value: 1.1, expiresOnDay: 0, description: 'Buena reputacion por servicio' }
        ],
      },
    ],
  },
  {
    type: 'social_media_viral',
    title: 'Video Viral',
    emoji: '📱',
    description: 'Un video de un punto increible en tu club se ha viralizado en redes sociales.',
    minDay: 7,
    probability: 0.03,
    minCourts: 1,
    options: [
      {
        text: 'Aprovechar y promocionar',
        effectDescription: '+0.5 reputacion, +30% demanda 7 dias',
        effects: [
          { source: 'Video viral', type: 'demand_modifier', value: 1.3, expiresOnDay: 0, description: '+30% demanda por viralidad' }
        ],
      },
      { text: 'No hacer nada especial', effectDescription: '+0.1 reputacion' },
    ],
  },
  {
    type: 'vandalism',
    title: 'Acto Vandalico',
    emoji: '🚨',
    description: 'Han pintado grafitis en las paredes del club durante la noche.',
    minDay: 10,
    probability: 0.03,
    minCourts: 1,
    options: [
      {
        text: 'Limpiar y poner camaras (€400)',
        effectDescription: 'No volvera a pasar',
        costMultiplier: 400,
      },
      {
        text: 'Solo limpiar (€100)',
        effectDescription: 'Puede repetirse',
        costMultiplier: 100,
        effects: [
          { source: 'Sin seguridad', type: 'reputation_modifier', value: 0.98, expiresOnDay: 0, description: '-2% reputacion por inseguridad' }
        ],
      },
    ],
  },
]

// Helper to get random weather based on probabilities
export function getRandomWeather(previousWeather: WeatherType): WeatherType {
  const transitions: Record<WeatherType, Record<WeatherType, number>> = {
    sunny: { sunny: 0.6, cloudy: 0.3, rainy: 0.08, stormy: 0.02 },
    cloudy: { sunny: 0.3, cloudy: 0.4, rainy: 0.25, stormy: 0.05 },
    rainy: { sunny: 0.15, cloudy: 0.35, rainy: 0.4, stormy: 0.1 },
    stormy: { sunny: 0.1, cloudy: 0.4, rainy: 0.4, stormy: 0.1 },
  }

  const probs = transitions[previousWeather]
  const random = Math.random()
  let cumulative = 0

  for (const [weather, prob] of Object.entries(probs)) {
    cumulative += prob
    if (random <= cumulative) {
      return weather as WeatherType
    }
  }

  return 'sunny'
}

// Check if an event should trigger
export function shouldTriggerEvent(def: EventDefinition, currentDay: number): boolean {
  if (currentDay < def.minDay) return false
  return Math.random() < def.probability
}

// Get a random event for the day (with state-based filtering)
export function getRandomEvent(
  currentDay: number,
  courtCount: number = 0,
  reputation: number = 0,
  buildingCount: number = 0,
  staffCount: number = 0,
): EventDefinition | null {
  const eligibleEvents = EVENT_DEFINITIONS.filter(e => {
    if (currentDay < e.minDay) return false
    if (e.minCourts && courtCount < e.minCourts) return false
    if (e.minReputation && reputation < e.minReputation) return false
    if (e.minBuildings && buildingCount < e.minBuildings) return false
    if (e.minStaff && staffCount < e.minStaff) return false
    return true
  })

  for (const event of eligibleEvents) {
    if (shouldTriggerEvent(event, currentDay)) {
      return event
    }
  }

  return null
}
